import React, { useCallback, useEffect, useState } from 'react';
import { Card, Container, Col, Row, Alert, Form } from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUtilityPole,
  faEnvelope,
  faBolt,
  faBoltSlash,
  faEye,
  faEyeSlash,
  faSyncAlt,
  faUpload,
  faMeter,
} from '@fortawesome/pro-solid-svg-icons';

import Loader from '../../components/Loader';

import NewProviderModal from '../../components/modals/ProviderModals/NewProviderModal';
import UrjanetLinkAccountModal from '../../components/modals/UrjanetLinkAccountModal';
import PlugLinkAccountModal from '../../components/modals/PlugLinkAccountModal';
import PlugUploadModal from '../modals/PlugActionModals/PlugUploadModal';
import UtilityTable from './UtilityTable';
import RequestCredentialModal from '../../components/modals/ProviderModals/RequestCredentialModal';
import { useOrganizationRoles } from '../../helpers/checkRoles';
import {
  getTitleName,
  handleGetLicenseKey,
  handleGetAllCredentials,
  handleRefreshAccountsData,
  handleRefreshLocalData,
  handleJoinLocalAccounts,
  handleGetUtlityTemplates,
  handleGetMeterGroups,
} from './helpers/provider';
import PlugCustomTemplates from './PlugCustomTemplates';
import PlugCustomTemplateAccounts from './PlugCustomTemplates/PlugCustomTemplateAccounts';
import PlugMeterReportModal from '../modals/PlugActionModals/PlugMeterReportModal';
import PlugCustomTemplateReports from './PlugCustomTemplates/PlugCustomTemplateReports';
import PlugMeterGroups from './PlugCustomTemplates/PlugMeterGroups';

function UtilityImportSettings(props) {
  const {
    organization,
    userSelectedOrganizationDetails,
    provider_type,
    OPAUpdate,
    visible,
    handleUpdateTables,
    showToast,
  } = props;

  const [bodyOption, setBodyOption] = useState('accounts');

  // state variables
  const [allCredentials, setAllCredentials] = useState([]);
  const [pseudoCredentials, setPseudoCredentials] = useState([]);
  const [credentialIds, setCredentialIds] = useState([]);
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [providerAccountsWithLocal, setProviderAccountsWithLocal] = useState(
    []
  );
  const [localUtilityAccounts, setLocalUtilityAccounts] = useState([]);
  const [localBuildings, setLocalBuildings] = useState([]);
  const [posr, setposr] = useState(null); //provider organization share recipient
  const [hiddenCredentials, setHiddenCredentials] = useState(null);
  const [utilityTemplates, setUtilityTemplates] = useState([]);
  const [meterGroups, setMeterGroups] = useState([]);

  //state flags
  const [showSecondary, setShowSecondary] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  //modal flags
  const [showNewProvider, setShowNewProvider] = useState(false);
  const [showUrjanetLinkModal, setShowUrjanetLinkModal] = useState(false);
  const [showPlugLinkModal, setShowPlugLinkModal] = useState(false);
  const [showRequestCredentialModal, setShowRequestCredentialModal] =
    useState(false);
  const [showPlugUploadModal, setShowPlugUploadModal] = useState(false);
  const [showPlugMeterModal, setShowPlugMeterModal] = useState(false);

  //licenses, loading, access
  const [activeLicense, setActiveLicense] = useState(false);
  const [isLoadingCreds, setIsLoadingCreds] = useState(true);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingMeterGroups, setIsLoadingMeterGroups] = useState(false);

  const [lastOPAUpdate, setLastOPAUpdate] = useState(null);
  const [firstUpdate, setFirstUpdate] = useState(0);
  const [manualRefresh, setManualRefresh] = useState(0);

  const updateAccess = useOrganizationRoles(
    userSelectedOrganizationDetails,
    'organization',
    'update'
  );

  useEffect(() => {
    let activeLicense = false;
    let license_key = handleGetLicenseKey(provider_type);
    if (organization.id) {
      if (
        organization &&
        organization.licenses &&
        license_key in organization.licenses
      ) {
        activeLicense = organization.licenses[license_key];
      }
    }
    setActiveLicense(activeLicense);
  }, [organization, provider_type]);

  useEffect(() => {
    if (organization.id) {
      setposr(organization.posr);
      setHiddenCredentials(organization.hidden_provider_credentials);
    }
  }, [
    organization.posr,
    organization.id,
    organization.hidden_provider_credentials,
  ]);

  // modal data
  const [urjanetLinkModalData, setUrjanetLinkModalData] = useState({
    id: null,
    enrolled: false,
    linkedAccount: null,
    accountNumber: '',
    utilityProvider: '',
    subscribe: true,
    pullHistory: true,
  });
  const [plugLinkModalData, setPlugLinkModalData] = useState({
    id: '',
    linkedAccount: '',
    accountNumber: '',
    utilityProvider: '',
    active: true,
    credentialId: null,
    localAccount: { id: '' },
  });

  // modal functions
  const handleCloseNewProviderModal = () => {
    setShowNewProvider(false);
    getAllCredentials();
  };

  const handleOpenLinkModal = (selectedRow, provider_type) => {
    let local_row = { ...selectedRow };
    local_row.newAccountId = local_row.localAccount
      ? local_row.localAccount.id
      : null;
    if (provider_type === 1) {
      setUrjanetLinkModalData(local_row);
      setShowUrjanetLinkModal(true);
    }
    if (provider_type === 2) {
      setPlugLinkModalData(local_row);
      setShowPlugLinkModal(true);
    }
  };

  //get all credentials functions
  const getAllCredentials = useCallback(() => {
    if (organization.id && posr && hiddenCredentials) {
      handleGetAllCredentials(
        setAllCredentials,
        setCredentialIds,
        organization.id,
        posr,
        hiddenCredentials,
        provider_type,
        setIsLoadingCreds,
        showToast
      );
    }
  }, [organization, posr, hiddenCredentials, showToast, provider_type]);

  useEffect(() => {
    getAllCredentials();
  }, [getAllCredentials]);

  // get account functions
  const refreshAccountsData = useCallback(() => {
    handleRefreshAccountsData(
      setPseudoCredentials,
      setProviderAccounts,
      setLocalUtilityAccounts,
      setLocalBuildings,
      organization.id,
      provider_type,
      credentialIds,
      setIsLoadingAccounts,
      showToast
    );
  }, [organization, provider_type, credentialIds, showToast]);

  useEffect(() => {
    handleJoinLocalAccounts(
      providerAccounts,
      localUtilityAccounts,
      provider_type,
      setProviderAccountsWithLocal
    );
  }, [providerAccounts, localUtilityAccounts, provider_type]);

  useEffect(() => {
    refreshAccountsData();
    setFirstUpdate(true);
  }, [refreshAccountsData]);

  useEffect(() => {
    if (
      firstUpdate &&
      OPAUpdate !== lastOPAUpdate &&
      visible &&
      organization.id
    ) {
      handleRefreshLocalData(
        setLocalUtilityAccounts,
        setLocalBuildings,
        organization.id,
        showToast
      );
      setLastOPAUpdate(OPAUpdate);
    }
    if (!firstUpdate) {
      setLastOPAUpdate(OPAUpdate);
    }
  }, [
    OPAUpdate,
    lastOPAUpdate,
    visible,
    firstUpdate,
    organization.id,
    showToast,
  ]);

  const handleRefreshTemplatesData = useCallback(() => {
    handleGetUtlityTemplates(
      provider_type,
      organization.id,
      setUtilityTemplates,
      setIsLoadingTemplates
    );
  }, [organization, provider_type]);

  useEffect(() => {
    handleRefreshTemplatesData();
  }, [handleRefreshTemplatesData]);

  const handleRefreshMeterGroups = useCallback(() => {
    handleGetMeterGroups(
      provider_type,
      organization.id,
      setMeterGroups,
      setIsLoadingMeterGroups
    );
  }, [organization, provider_type]);

  useEffect(() => {
    handleRefreshMeterGroups();
  }, [handleRefreshMeterGroups]);

  useEffect(() => {
    if (manualRefresh > 0) {
      getAllCredentials();
      handleRefreshTemplatesData();
      handleRefreshMeterGroups();
      setManualRefresh(0);
    }
  }, [
    manualRefresh,
    handleRefreshTemplatesData,
    refreshAccountsData,
    handleRefreshMeterGroups,
    getAllCredentials,
  ]);

  const getStyles = (disabled) => {
    return {
      iconStyle: {
        fontSize: '20px',
        margin: '0 0.35em',
        verticalAlign: 'text-bottom',
        cursor: disabled ? 'none' : 'pointer',
        color: 'var(--et_tab_grey)',
        opacity: disabled ? '0.5' : '1.0',
      },
      actionIconStyle: {
        fontSize: '20px',
        margin: '0 0.35em',
        verticalAlign: 'text-bottom',
        cursor: disabled ? 'none' : 'pointer',
        color: 'var(--et_tab_grey)',
        opacity: disabled ? '0.5' : '1.0',
        display: 'inline-block',
      },
    };
  };

  return (
    <Card.Body>
      {!activeLicense && (
        <Alert style={{ width: '90%' }} variant='danger'>
          Warning: This license is expired. Please contact an administrator
          about renewing this license.
        </Alert>
      )}
      <Row>
        <Col style={{ marginBottom: '0.5em' }}>
          {provider_type === 2 && (
            <div style={{ display: 'inline-block' }}>
              Action:{'  '}
              <Form style={{ width: '250px', display: 'inline-block' }}>
                <Form.Control
                  as='select'
                  value={bodyOption}
                  onChange={(e) => setBodyOption(e.target.value)}
                >
                  <option key={'accounts'} value={'accounts'}>
                    {'Manage ' + getTitleName(provider_type) + ' Accounts'}
                  </option>
                  <option key={'create_templates'} value={'create_templates'}>
                    {'Create ' + getTitleName(provider_type) + ' Templates'}
                  </option>
                  <option key={'meter_groups'} value={'meter_groups'}>
                    {'Create ' + getTitleName(provider_type) + ' Meter Groups'}
                  </option>
                  <option key={'template_report'} value={'template_report'}>
                    {'Run ' + getTitleName(provider_type) + ' Template Reports'}
                  </option>
                  <option key={'manage_templates'} value={'manage_templates'}>
                    {'Manage ' + getTitleName(provider_type) + ' Templates'}
                  </option>
                </Form.Control>
              </Form>
            </div>
          )}
          {(provider_type !== 2) && (
            <div style={{ display: 'inline-block' }}>
              <h5 style={{ marginBottom: 0 }}>
                {'Manage ' + getTitleName(provider_type) + ' Accounts'}
              </h5>
            </div>
          )}
          <FontAwesomeIcon
            style={getStyles(isLoadingCreds).actionIconStyle}
            icon={faSyncAlt}
            title={'Refresh Data'}
            disabled={isLoadingCreds}
            onClick={() => setManualRefresh(1)}
          />
        </Col>
      </Row>

      <div
        style={{
          display: bodyOption !== 'accounts' ? 'none' : 'block',
        }}
      >
        <Row>
          <Col style={{ marginBottom: '0.5em' }} xs={12}>
            {updateAccess && activeLicense && (
              <div style={{ textAlign: 'right' }}>
                {provider_type === 2 && (
                  <FontAwesomeIcon
                    style={getStyles(isLoadingCreds).iconStyle}
                    icon={faMeter}
                    title={'Download Meters'}
                    disabled={isLoadingCreds}
                    onClick={() => setShowPlugMeterModal(true)}
                  />
                )}
                {provider_type === 2 && (
                  <FontAwesomeIcon
                    style={getStyles(isLoadingCreds).iconStyle}
                    icon={faUpload}
                    title={'Upload Files'}
                    disabled={isLoadingCreds}
                    onClick={() => setShowPlugUploadModal(true)}
                  />
                )}
              </div>
            )}
          </Col>
        </Row>
        <Row></Row>
        <Row>
          <Col style={{ marginBottom: '0.5em' }} xs={12}>
            {updateAccess && activeLicense && (
              <div style={{ textAlign: 'right' }}>
                <FontAwesomeIcon
                  style={getStyles(isLoadingCreds).iconStyle}
                  icon={faEnvelope}
                  title={'Request Credentials'}
                  disabled={isLoadingCreds}
                  onClick={() => setShowRequestCredentialModal(true)}
                />
                <FontAwesomeIcon
                  style={getStyles(isLoadingCreds).iconStyle}
                  icon={faUtilityPole}
                  title={'Add New Provider'}
                  disabled={isLoadingCreds}
                  onClick={() => setShowNewProvider(true)}
                />
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col>
            <div
              style={{
                textAlign: 'right',
                paddingBottom: '0.5em',
              }}
            >
              <FontAwesomeIcon
                style={getStyles(isLoadingCreds).iconStyle}
                icon={showHidden ? faEyeSlash : faEye}
                title={'Show Hidden Credentials'}
                disabled={isLoadingCreds}
                onClick={() => setShowHidden((prev) => !prev)}
              />
              <FontAwesomeIcon
                style={getStyles(isLoadingCreds).iconStyle}
                icon={showSecondary ? faBoltSlash : faBolt}
                title={'Show Secondary Account'}
                disabled={isLoadingCreds}
                onClick={() => setShowSecondary((prev) => !prev)}
              />
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            {isLoadingCreds ? (
              <LocalLoader />
            ) : (
              <UtilityTable
                organizationId={organization.id}
                allCredentials={allCredentials}
                pseudoCredentials={pseudoCredentials}
                providerAccounts={providerAccountsWithLocal}
                provider_type={provider_type}
                showSecondary={showSecondary}
                showHidden={showHidden}
                handleOpenLinkModal={handleOpenLinkModal}
                isLoading={isLoadingAccounts}
                setAllCredentials={setAllCredentials}
                showToast={showToast}
                updateAccess={updateAccess}
                activeLicense={activeLicense}
              />
            )}
          </Col>
        </Row>
      </div>
      <NewProviderModal
        organizationId={organization.id}
        show={showNewProvider}
        onHide={handleCloseNewProviderModal}
        provider_type={provider_type}
      />
      <UrjanetLinkAccountModal
        show={showUrjanetLinkModal}
        onHide={() => setShowUrjanetLinkModal(false)}
        modalData={urjanetLinkModalData}
        localUtilityAccounts={localUtilityAccounts}
        organization={organization}
        buildings={localBuildings}
        provider_type={1}
        setProviderAccounts={setProviderAccounts}
        setLocalUtilityAccounts={setLocalUtilityAccounts}
        handleUpdateTables={handleUpdateTables}
        showToast={showToast}
        activeLicense={activeLicense}
      />
      <PlugLinkAccountModal
        show={showPlugLinkModal}
        onHide={() => setShowPlugLinkModal(false)}
        modalData={plugLinkModalData}
        localUtilityAccounts={localUtilityAccounts}
        organization={organization}
        buildings={localBuildings}
        provider_type={2}
        setProviderAccounts={setProviderAccounts}
        setLocalUtilityAccounts={setLocalUtilityAccounts}
        handleUpdateTables={handleUpdateTables}
        showToast={showToast}
        activeLicense={activeLicense}
      />
      <RequestCredentialModal
        show={showRequestCredentialModal}
        onHide={() => setShowRequestCredentialModal(false)}
        organizationId={organization.id}
        showToast={showToast}
        variant='new'
        provider_type={provider_type}
      />
      <PlugUploadModal
        show={showPlugUploadModal}
        onHide={() => {
          setShowPlugUploadModal(false);
        }}
        organizationUUID={organization.uuid}
      />
      <PlugMeterReportModal
        show={showPlugMeterModal}
        onHide={() => {
          setShowPlugMeterModal(false);
        }}
        organizationId={organization.id}
        allCredentials={allCredentials}
      />

      <div
        style={{
          display: provider_type === 2 && bodyOption === 'create_templates' ? 'block' : 'none',
        }}
      >
        {(isLoadingTemplates || isLoadingAccounts) && <LocalLoader />}
        {!(isLoadingTemplates || isLoadingAccounts) && (
          <PlugCustomTemplates
            organizationId={organization.id}
            localUtilityAccounts={localUtilityAccounts}
            providerAccounts={providerAccounts}
            templates={utilityTemplates}
            setTemplates={setUtilityTemplates}
            showToast={showToast}
          />
        )}
      </div>


      <div style={{
        display: provider_type === 2 && bodyOption === 'meter_groups' ? 'block' : 'none',
      }}>
        {(isLoadingMeterGroups || isLoadingCreds) && <LocalLoader />}
        {!(isLoadingMeterGroups || isLoadingCreds) && (
          <PlugMeterGroups
            organizationId={organization.id}
            allCredentials={allCredentials}
            meterGroups={meterGroups}
            setMeterGroups={setMeterGroups}
            showToast={showToast}
          />
        )}
      </div>
      <div style={{
        display: provider_type === 2 && bodyOption === 'template_report' ? 'block' : 'none',
      }}>
        {(isLoadingTemplates || isLoadingAccounts) && <LocalLoader />}
        {!(isLoadingTemplates || isLoadingAccounts) && (
          <PlugCustomTemplateReports
            organizationId={organization.id}
            organizationName={organization.name}
            templates={utilityTemplates}
            meterGroups={meterGroups}
            showToast={showToast}
          />
        )}
      </div>
      <div style={{
        display: provider_type === 2 && bodyOption === 'manage_templates' ? 'block' : 'none',
      }}>
        {(isLoadingTemplates || isLoadingAccounts) && <LocalLoader />}
        {!(isLoadingTemplates || isLoadingAccounts) && (
          <PlugCustomTemplateAccounts
            organizationId={organization.id}
            localUtilityAccounts={localUtilityAccounts}
            setLocalUtilityAccounts={setLocalUtilityAccounts}
            templates={utilityTemplates}
            showToast={showToast}
          />
        )}
      </div>
    </Card.Body>
  );
}

export default UtilityImportSettings;

function LocalLoader() {
  return (
    <Container>
      <Row>
        <Col
          style={{
            paddingBottom: '10vh',
            paddingTop: '5vh',
            paddingLeft: '49%',
          }}
        >
          <Loader />
        </Col>
      </Row>
    </Container>
  );
}
